import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { MinAvgMaxKeys } from "@repo/utils";
import { checkIfLibraryElementExistsQuery, moveLibraryElementQuery } from "@query";
import { useEstimateEditorStore } from "@tools";
import { useEditorWebsocket } from "@hooks";

export const useLibraryPusher = ({
  element,
  tableDepth,
}) => {
  const { t } = useTranslation();
  const editorStore = useEstimateEditorStore();
  const socket = useEditorWebsocket();
  
  const [ isLoading, setLoading ] = useState(false);
  const [ libraryPushAlertStatus, setLibraryPushAlertStatus ] = useState(null);
  const [ libraryPushSuccess, setLibraryPushSuccess ] = useState(false);
  
  const addToLibrary = async () => {
    if (isLoading) return;
    
    setLoading(true);
    
    if (!element.name?.length) {
      setLibraryPushAlertStatus("alerts.library.library_name_required");
      setLoading(false);
      return;
    }
    
    if (element.componentId) {
      const exists = await checkIfLibraryElementExistsQuery(
        element.componentId
      );
      if (exists) {
        setLibraryPushAlertStatus("alerts.library.library_is_already");
        setLoading(false);
        return;
      } else element.setComponentId();
    }
    
    let newElement = element;
    const updateComponentIds = (element, result) => {
      element.setComponentId(result.componentId);
      if (element.children?.length && result.children?.length)
        element.children.forEach((child, index) =>
          updateComponentIds(child, result.children[index])
        );
    };
    
    try {
      const customWorkTypes = editorStore.metadataWorkTypes
        .filter(wT => {
          const f = editorStore.workTypes.find(sWT => wT.id === sWT.id);
          return !f || f.custom;
        })
        .map(({ id, name, color }) => ({ oldId: id, name, color }));
      
      if(customWorkTypes.length && socket)
        await socket.requestVersionWorkTypesChange(customWorkTypes);
      
      let parentComponentId =
        tableDepth > 1 ? element.parent?.componentId : undefined; // getParentLibraryStatus(element.parent);
      
      const serializedElement = newElement.serialize(t("common.unnamed"), true);

      const removeAutomatedValues = (el) => {
        if(el.values)
          Object.keys(el.values).forEach(id => {
            const hasValue = Boolean(
              [MinAvgMaxKeys.MIN, MinAvgMaxKeys.AVG, MinAvgMaxKeys.MAX].find(key => (
                typeof el.values[id][key] === "number" && el.values[id][key] > 0
              ))
            );
            const hasPercent = Boolean(
              element.percentageWorkTypes.find(wT => wT.id === Number(id))
              && !( el.values[id].avgOv || el.values[id].minMaxOv )
            );

            if(!hasValue || hasPercent)
              delete el.values[id];
          })

        if(el.children?.length)
          el.children.forEach((child) => removeAutomatedValues(child));
      }

      removeAutomatedValues(serializedElement);
      
      const updatedElement = await moveLibraryElementQuery(
        serializedElement,
        tableDepth,
        parentComponentId
      );

      updateComponentIds(newElement, updatedElement);
      setLibraryPushSuccess(true);
    } catch (e) {
      console.error(e);
      setLibraryPushAlertStatus("alerts.library.library_name_error");
    }
    
    setLoading(false);
  };
  
  const closeAlert = useCallback(() => setLibraryPushAlertStatus(false), []);
  const closeSuccessAlert = useCallback(() => setLibraryPushSuccess(false), []);
  
  return {
    isLoading,
    libraryPushAlertStatus,
    libraryPushSuccess,
    addToLibrary,
    closeAlert,
    closeSuccessAlert,
  }
}